'use client';

import { TableV2 } from '@medeloop/medeloop-components-library/dist/components/Table/V2';
import * as React from 'react';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetCodesets } from 'src/api/codesets-queries';
import { NavigateBack, TitleSubTitle } from 'src/components';
import { CodeItem } from 'src/types/codesets';

const BASE_DATASETS_URL = '/account/data/datasets';

interface InfoSectionProps {
  label: string;
  children: React.ReactNode;
}

interface TimelineInfoProps {
  email: string;
  date: string;
}

const Divider = () => {
  return <div className="w-px bg-gray-800 h-[46px] max-md:hidden" aria-hidden="true" />;
};

const InfoSection: React.FC<InfoSectionProps> = ({ label, children }) => {
  return (
    <section className="flex flex-col gap-1 items-start">
      <h2 className="text-sm font-semibold leading-5 text-gray-400 max-sm:text-xs">{label}</h2>
      <p className="text-base leading-6 text-gray-100 max-sm:text-sm">{children}</p>
    </section>
  );
};

const TimelineInfo: React.FC<TimelineInfoProps> = ({ email, date }) => {
  return (
    <article className="flex gap-4 items-center p-4 mx-auto max-w-none bg-transparent rounded-md border border-stroke-on-dark-subdued border-solid max-md:flex-col max-md:items-start max-md:max-w-[991px] max-sm:p-3 max-sm:max-w-screen-sm">
      <InfoSection label="Creator">{email}</InfoSection>

      <Divider />

      <InfoSection label="Creation date">{date}</InfoSection>
    </article>
  );
};

export function CodeDetailsPage() {
  const { codesetId } = useParams();
  const { data, isLoading } = useGetCodesets(codesetId);

  // Directly use the data returned by the API
  const codeset = data?.data?.codesets[0];

  const headers = [
    { label: 'Code', key: 'code' },
    { label: 'Concept Name', key: 'conceptName' },
    { label: 'N', key: 'count' },
    { label: 'Relevance', key: 'relevance' },
  ];

  const transformedData = useMemo(() => {
    if (!codeset?.data) return [];

    return codeset.data.map((code: CodeItem) => ({
      id: code.concept_id,
      code: code.concept_id,
      conceptName: code.concept_name,
      count: code.concept_count || 0,
      relevance: code.relevance ? `${Math.round(code.relevance * 100)}%` : 'N/A',
    }));
  }, [codeset]);

  return (
    <section className="flex flex-col">
      <div className="bg-dark-subdued-background border-b border-stroke-on-dark-subdued pb-4">
        <div className="max-w-right-section mx-auto px-6 xl:px-0">
          <NavigateBack to={BASE_DATASETS_URL} label="Codes" />
          <div className="flex items-center justify-between mb-4">
            <TitleSubTitle
              title={codeset?.codesetName || 'Code Details'}
              subTitle={codeset?.codesetDescription || 'Loading code information...'}
              isLoading={isLoading}
            />
          </div>
          <div>
            <TimelineInfo email={codeset?.author || ''} date={codeset?.createdAt || ''} />
          </div>
        </div>
      </div>

      <div className="max-w-right-section w-full mx-auto px-6 xl:px-0">
        <div className="mt-8 pb-8">
          <TableV2 headers={headers} data={transformedData} isLoading={isLoading} />
        </div>
      </div>
    </section>
  );
}
